import { useQuery, useQueryClient } from '@tanstack/react-query';
import { payslipKeys } from './queryKeys';
import { payslipService } from '@/services/payslips';
import type { PayslipWithLineItems, PayslipDetailsResponse } from '@/services/payslips/types';

interface PayslipCacheConfig {
  staleTime?: number;
  cacheTime?: number;
  refetchOnWindowFocus?: boolean;
}

const DEFAULT_CONFIG: PayslipCacheConfig = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  cacheTime: 10 * 60 * 1000, // 10 minutes
  refetchOnWindowFocus: false,
};

export function usePayslipCache() {
  const queryClient = useQueryClient();

  const prefetchPayslipDetails = async (payslipId: string, config?: PayslipCacheConfig) => {
    const mergedConfig = { ...DEFAULT_CONFIG, ...config };
    
    await queryClient.prefetchQuery({
      queryKey: payslipKeys.detail(payslipId),
      queryFn: () => payslipService.getPayslipDetails(payslipId),
      staleTime: mergedConfig.staleTime,
    });
  };

  const prefetchPayslipLineItems = async (payslipId: string, config?: PayslipCacheConfig) => {
    const mergedConfig = { ...DEFAULT_CONFIG, ...config };
    
    await queryClient.prefetchQuery({
      queryKey: payslipKeys.lineItems(payslipId),
      queryFn: () => payslipService.getPayslipLineItems(payslipId),
      staleTime: mergedConfig.staleTime,
    });
  };

  const getCachedPayslipDetails = (payslipId: string): PayslipDetailsResponse | undefined => {
    return queryClient.getQueryData(payslipKeys.detail(payslipId));
  };

  const getCachedPayslipLineItems = (payslipId: string) => {
    return queryClient.getQueryData(payslipKeys.lineItems(payslipId));
  };

  const invalidatePayslipCache = (payslipId?: string) => {
    if (payslipId) {
      queryClient.invalidateQueries({ queryKey: payslipKeys.detail(payslipId) });
      queryClient.invalidateQueries({ queryKey: payslipKeys.lineItems(payslipId) });
    } else {
      queryClient.invalidateQueries({ queryKey: payslipKeys.all });
    }
  };

  const preloadVisiblePayslips = async (payslipIds: string[], config?: PayslipCacheConfig) => {
    const prefetchPromises = payslipIds.map(id => prefetchPayslipDetails(id, config));
    await Promise.allSettled(prefetchPromises);
  };

  const warmCache = async (profileId: string) => {
    // Prefetch the main payslips list
    await queryClient.prefetchQuery({
      queryKey: payslipKeys.list(profileId),
      queryFn: () => payslipService.getPayslips(profileId),
      staleTime: DEFAULT_CONFIG.staleTime,
    });

    // Get the cached payslips to prefetch their details
    const payslipsData = queryClient.getQueryData(payslipKeys.list(profileId)) as any;
    if (payslipsData?.payslips) {
      // Prefetch details for the first few payslips (most likely to be viewed)
      const recentPayslips = payslipsData.payslips.slice(0, 5);
      await preloadVisiblePayslips(
        recentPayslips.map((p: any) => p.id),
        { staleTime: 15 * 60 * 1000 } // Cache recent payslips longer
      );
    }
  };

  return {
    prefetchPayslipDetails,
    prefetchPayslipLineItems,
    getCachedPayslipDetails,
    getCachedPayslipLineItems,
    invalidatePayslipCache,
    preloadVisiblePayslips,
    warmCache,
  };
}

export function useCachedPayslipDetails(payslipId: string | null, config?: PayslipCacheConfig) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  return useQuery({
    queryKey: payslipKeys.detail(payslipId || ''),
    queryFn: () => payslipService.getPayslipDetails(payslipId!),
    enabled: !!payslipId,
    staleTime: mergedConfig.staleTime,
    gcTime: mergedConfig.cacheTime,
    refetchOnWindowFocus: mergedConfig.refetchOnWindowFocus,
  });
}

export function useCachedPayslipLineItems(payslipId: string | null, config?: PayslipCacheConfig) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  return useQuery({
    queryKey: payslipKeys.lineItems(payslipId || ''),
    queryFn: () => payslipService.getPayslipLineItems(payslipId!),
    enabled: !!payslipId,
    staleTime: mergedConfig.staleTime,
    gcTime: mergedConfig.cacheTime,
    refetchOnWindowFocus: mergedConfig.refetchOnWindowFocus,
  });
}